import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupIcon from "@mui/icons-material/Group";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useGetItemsByBusinessQuery } from "lib/features/item/itemService";
import { Item } from "lib/features/item/types";
import { getBusinessIdAsNumberFn } from "lib/hooks/queries/useGetCurrentBusiness";
import { useEffect, useState } from "react";
import { getItemImageSource } from "util/getImageSource";

const AllAvailabilitiesPopup = ({ handleAddAvailabilityDialogOpen, setSelectedItem }: { handleAddAvailabilityDialogOpen: () => void, setSelectedItem: (item: Item) => void }) => {
    const [businessId, setBusinessId] = useState<number | null>(null);
    const [isBusinessIdLoading, setIsBusinessIdLoading] = useState<boolean>(true);

    const {
        data: items,
        isLoading: itemsLoading,
    } = useGetItemsByBusinessQuery(businessId!, {
        skip: !businessId,
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        const fetchBusinessId = async () => {
            try {
                const id = await getBusinessIdAsNumberFn();
                setBusinessId(id);
            } catch (error) {
                console.log("Error fetching business ID:", error);
            } finally {
                setIsBusinessIdLoading(false);
            }
        };

        fetchBusinessId();
    }, []);

    // Filter for Referee items only (item_type 1 or 3)
    const refereeItems = items?.filter((i: Item) => i.item_type === 1 || i.item_type === 3) || [];

    // Helper to format duration
    const formatDuration = (minutes: number | null | undefined): string => {
        if (!minutes) return "-";
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
        if (hours > 0) return `${hours} hr`;
        return `${mins}m`;
    };

    if (itemsLoading || isBusinessIdLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, width: "100%", overflowX: "hidden" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (refereeItems.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography>No items found. Create an item first.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 5, width: "100%", overflowX: "hidden" }}>
            {refereeItems.map((item: Item) => {
                const details = item.itemDetails?.[0];
                const availabilityCount = item.availabilities?.length || 0;

                return (
                    <Box
                        onClick={() => { handleAddAvailabilityDialogOpen(); setSelectedItem(item) }}
                        key={item.id}
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "12px",
                            width: "288px",
                            maxHeight: "332px",
                            cursor: "pointer",
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            },
                        }}
                    >
                        {/* Item Image */}
                        <Box
                            component="img"
                            src={getItemImageSource(item)}
                            alt={details?.item_name || "Item"}
                            sx={{
                                width: "100%",
                                height: "162px",
                                objectFit: "cover",
                            }}
                        />

                        {/* Card Content */}
                        <Box sx={{ p: 1.5 }}>
                            {/* Item Name */}
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "17px",
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                }}
                            >
                                {details?.item_name || "Unnamed Item"}
                            </Typography>

                            {/* Description */}
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    color: "text.secondary",
                                    lineHeight: 1.3,
                                    mb: 1,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    minHeight: "32px",
                                }}
                            >
                                {details?.headline || "No description"}
                            </Typography>

                            {/* Price, Capacity, Duration Row */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    fontSize: "11px",
                                    color: "text.secondary",
                                    mb: 1,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                    <LocalOfferIcon sx={{ fontSize: "14px", color: "#666" }} />
                                    <Typography sx={{ fontSize: "15px" }}>
                                        ${details?.price || 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                    <GroupIcon sx={{ fontSize: "15px", color: "#666" }} />
                                    <Typography sx={{ fontSize: "15px" }}>
                                        {details?.capacity || 0} max
                                    </Typography>
                                </Box>
                                {
                                    details?.duration && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                            <AccessTimeIcon sx={{ fontSize: "15px", color: "#666" }} />
                                            <Typography sx={{ fontSize: "15px" }}>
                                                {formatDuration(parseInt(details?.duration || "0"))}
                                            </Typography>
                                        </Box>
                                    )
                                }
                            </Box>

                            {/* Availabilities Link */}
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    color: "#2E7D32",
                                    cursor: "pointer",
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {availabilityCount} Availabilities
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default AllAvailabilitiesPopup;
